import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import ConfirmModal from "../../../../components/ConfirmModal";
import * as Fonts from "../../../../components/Fonts";
import { AiTextArea } from "../../../../components/Form/AiTextArea";
import Input from "../../../../components/Form/Input/input";
import Label from "../../../../components/Form/Label";
import { FormGroup } from "../../../../components/Form/styles";
import SaveBar from "../../../../components/SaveBar";
import { SubHeader } from "../../../../components/SubHeader";
import { useCurrentPage, withPage } from "../../../../hooks/CurrentPage";
import ChevronRightIcon from "../../../../icons/ChevronRight";
import {
  FormCustomField,
  fieldsOf,
  hasContactField,
  hasEmailField,
  legacyFieldsOf,
  variantOf,
} from "../../../../model/formFields";
import { FormSection } from "../../../../model/site";
import { clone, deepEqual } from "../../../../utils/objects";
import { sanitizeUrl } from "../../../../utils/url";
import ItemsTable from "../../components/ItemsTable";
import { useSection } from "../../hooks/Section";
import { navigationState } from "../Sections/navigationState";
import { FieldTypeIcon } from "./components/FieldTypeIcon";
import { FormNavigationState } from "./formNavigationState";
import { Card, Section } from "./pages/styles";
import { Container, StorageList, Warning } from "./styles";

const FormPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // sub-screens keep the page of the Keepo being edited (?page)
  const { pageId } = useCurrentPage();

  const formNavigationState = useRef(navigationState.get() as FormNavigationState);
  const [fieldIdRemoving, setFieldIdRemoving] = useState<string>();

  const restoreState = useCallback(
    (newly: unknown, section: FormSection) => {
      if (formNavigationState.current?.section) {
        return formNavigationState.current.section;
      }
      if (newly) {
        section.pageTitle = t("pages.editor.form.form.pageTitle.defaultValue");
        section.storage = {
          email: { active: false },
          mailChimp: { active: false },
          googleDrive: { active: false },
          whatsapp: { active: false },
        };
        section.buttonText = t("pages.editor.form.form.buttonTitle.defaultValue");
      }
      // Forms saved before custom fields get their fixed fields as a list (a new form gets all four)
      const customFields = fieldsOf(section, t);
      return { ...section, customFields, fields: legacyFieldsOf(customFields) };
    },
    [t, formNavigationState]
  );

  const { section, setSection, saveSection, hasUnsavedChanges } = useSection<FormSection>("form", restoreState);
  const fields = section.customFields || [];

  const setFields = (customFields: FormCustomField[]) =>
    setSection({ ...section, customFields, fields: legacyFieldsOf(customFields) });

  const saveHandler = () => {
    saveSection({
      ...section,
      successRedirectLink: sanitizeUrl(section.successRedirectLink),
    });
  };

  // Field and storage screens edit a copy of the section and hand it back through the navigation state
  const openSubScreen = (path: string, editingFieldId?: string) => {
    const state: FormNavigationState = {
      section: clone(section),
      defaultSection: formNavigationState.current?.defaultSection || clone<FormSection>(section),
      editingFieldId,
    };
    navigationState.set(state);
    navigate(withPage(path, pageId));
  };

  const removeField = () => {
    setFields(fields.filter(field => field.id !== fieldIdRemoving));
    setFieldIdRemoving(undefined);
  };

  const hasUnsavedChangesWrapper = (): boolean => {
    return formNavigationState?.current?.defaultSection
      ? !deepEqual(section, formNavigationState.current.defaultSection)
      : hasUnsavedChanges();
  };

  const { storage } = section;
  const hasStorage =
    storage.email.active || storage.googleDrive.active || storage.mailChimp.active || !!storage.whatsapp?.active;
  const isMissingContact = !hasContactField(fields);
  const isMissingMailChimpEmail = storage.mailChimp.active && !hasEmailField(fields);

  const saveDisabled =
    !section.buttonText ||
    fields.some(field => !field.label.trim()) ||
    isMissingContact ||
    isMissingMailChimpEmail ||
    !hasStorage;

  const prefix = "pages.editor.form";

  const storageItems = [
    { key: "email", title: "E-mail", active: storage.email.active, path: "/editor/form/storage/email" },
    {
      key: "whatsapp",
      title: "WhatsApp",
      active: !!storage.whatsapp?.active,
      path: "/editor/form/storage/whatsapp",
    },
    { key: "mailChimp", title: "Mailchimp", active: storage.mailChimp.active, path: "/editor/form/storage/mailchimp" },
  ];

  return (
    <Container>
      <SubHeader
        title={t(`${prefix}.header.title`)}
        crumbs={[{ label: t("pages.editor.preview.footer.actions.sections"), to: "/editor/sections" }]}
        hasUnsavedChanges={hasUnsavedChangesWrapper}
      />

      <Section>
        <Fonts.Label>{t(`${prefix}.sections.info`)}</Fonts.Label>
        <Card>
          <p className="lead">{t(`${prefix}.help`)}</p>
          <FormGroup>
            <Input
              id="pageTitle"
              type="text"
              value={section.pageTitle}
              label={t(`${prefix}.form.pageTitle.label`)}
              onChange={e => setSection({ ...section, pageTitle: e.target.value })}
            />
          </FormGroup>
        </Card>
      </Section>

      <Section>
        <ItemsTable
          title={t(`${prefix}.sections.fields`)}
          addButtonTitle={t(`${prefix}.fields.add`)}
          withVisibility
          items={fields.map(field => ({
            id: field.id,
            active: field.active,
            title: field.label || t(`${prefix}.fields.untitled`),
            icon: <FieldTypeIcon type={field.type} />,
            description: [
              t(`${prefix}.fieldTypes.${field.type}`),
              variantOf(field) && t(`${prefix}.fieldVariants.${field.type}.${variantOf(field)}`),
              field.required ? t(`${prefix}.fields.required`) : undefined,
            ]
              .filter(Boolean)
              .join(" · "),
          }))}
          onAddClick={() => openSubScreen("/editor/form/field", "new")}
          onEditClick={id => openSubScreen("/editor/form/field", id)}
          onRemoveClick={setFieldIdRemoving}
          onSortUpdate={items =>
            setFields(
              items.map(item => ({
                ...(fields.find(field => field.id === item.id) as FormCustomField),
                active: item.active,
              }))
            )
          }
        />
        {isMissingContact && <Warning>{t(`${prefix}.fields.warnings.contact`)}</Warning>}
        {isMissingMailChimpEmail && <Warning>{t(`${prefix}.fields.warnings.mailChimpEmail`)}</Warning>}
      </Section>

      <Section>
        <Fonts.Label>{t(`${prefix}.sections.button`)}</Fonts.Label>
        <Card>
          <FormGroup>
            <Input
              id="buttonText"
              type="text"
              required
              value={section.buttonText}
              label={t(`${prefix}.form.buttonTitle.label`)}
              onChange={e => setSection({ ...section, buttonText: e.target.value })}
              maxLength={24}
            />
          </FormGroup>
          <FormGroup>
            <AiTextArea
              id="successMessage"
              name="successMessage"
              autoComplete="off"
              rows={5}
              value={section.successMessage}
              placeholder={t(`${prefix}.form.successMessage.defaultValue`)}
              onChange={successMessage => setSection({ ...section, successMessage })}
              aiField="form.successMessage"
            />
            <Label htmlFor="successMessage" text={t(`${prefix}.form.successMessage.label`)} />
          </FormGroup>
          <FormGroup>
            <Input
              id="successRedirectLink"
              autoCapitalize="none"
              placeholder="https://"
              type="text"
              value={section.successRedirectLink}
              label={t(`${prefix}.form.successRedirectUrl`)}
              onChange={e => setSection({ ...section, successRedirectLink: e.target.value })}
            />
          </FormGroup>
        </Card>
      </Section>

      <Section>
        <Fonts.Label>{t(`${prefix}.form.storage.label`)}</Fonts.Label>
        <StorageList>
          {storageItems.map(item => (
            <li key={item.key}>
              <button type="button" onClick={() => openSubScreen(item.path)}>
                <span className="text">
                  <span className="title">{item.title}</span>
                  <span className="description">{t(`${prefix}.form.storage.${item.key}.menuTitle`)}</span>
                </span>
                {item.active && <span className="active">{t(`${prefix}.form.storage.status.active`)}</span>}
                <span className="chevron">
                  <ChevronRightIcon width={12} height={12} color="currentColor" />
                </span>
              </button>
            </li>
          ))}
        </StorageList>
        {!hasStorage && <Warning>{t(`${prefix}.fields.warnings.storage`)}</Warning>}
      </Section>

      <SaveBar sticky onSave={saveHandler} disabled={saveDisabled} hasUnsavedChanges={hasUnsavedChangesWrapper} />

      {fieldIdRemoving && (
        <ConfirmModal
          type="DANGER"
          title={t(`${prefix}.fields.remove.confirmModal.title`)}
          cancelTitle={t(`${prefix}.fields.remove.confirmModal.cancel`)}
          confirmButtonTitle={t(`${prefix}.fields.remove.confirmModal.confirm`)}
          onCancel={() => setFieldIdRemoving(undefined)}
          onConfirm={removeField}
        />
      )}
    </Container>
  );
};

export default FormPage;
